import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Zap, 
  WifiOff, 
  FileCheck2, 
  CloudCheck, 
  CheckCircle2, 
  ArrowUpRight, 
  FileText, 
  ShieldCheck, 
  Smartphone,
  Sparkles,
  Download,
  Share2
} from 'lucide-react';


// Pricing constants
const MONTHLY_PRICE = 19.90;
const ANNUAL_PRICE = 199.90;
const ANNUAL_MONTHLY_EQUIV = ANNUAL_PRICE / 12; // ~16.66
const DISCOUNT_PCT = Math.round((1 - ANNUAL_PRICE / (MONTHLY_PRICE * 12)) * 100); // 16%
const ANNUAL_SAVINGS = (MONTHLY_PRICE * 12 - ANNUAL_PRICE).toFixed(2).replace('.', ',');

function PricingSection() {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section className="space-y-8 max-w-4xl mx-auto">
      {/* Header — simple, no toggle here */}
      <div className="text-center space-y-2">
        <span className="text-xs font-mono text-[#F59E0B] tracking-widest">// PLANOS DE UTILIZAÇÃO</span>
        <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
          Escolha o plano ideal para seu negócio.
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 font-mono text-xs">

        {/* Free Plan */}
        <div className="bg-[#0A0D15] p-8 border border-gray-800 space-y-6 flex flex-col justify-between">
          <div className="space-y-4">
            <span className="text-gray-400 font-bold block">// PLANO GRATUITO</span>
            <div className="text-3xl font-extrabold text-white">
              R$ 0 <span className="text-xs font-normal text-gray-400">/mês</span>
            </div>
            <p className="text-gray-400 font-sans text-xs leading-relaxed">
              Comece gratuitamente e envie seus primeiros orçamentos no mês.
            </p>
            <ul className="space-y-3 pt-4 border-t border-gray-800 font-sans text-xs text-gray-300">
              <li className="flex items-center space-x-2">
                <CheckCircle2 size={16} className="text-[#10B981]" />
                <span>5 orçamentos por mês</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle2 size={16} className="text-[#10B981]" />
                <span>Envio direto no WhatsApp</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle2 size={16} className="text-[#10B981]" />
                <span>Funcionamento 100% offline</span>
              </li>
            </ul>
          </div>
          <a
            href="#download"
            className="w-full text-center bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 transition-colors block"
          >
            COMEÇAR GRATUITAMENTE
          </a>
        </div>

        {/* Pro Plan */}
        <div className="bg-[#0C111A] p-8 border-2 border-[#10B981] space-y-6 relative flex flex-col justify-between shadow-xl shadow-[#10B981]/10">
          <div className="absolute top-0 right-0 bg-[#10B981] text-[#07090E] font-bold text-[10px] px-3 py-1 uppercase">
            RECOMENDADO
          </div>

          <div className="space-y-4">
            {/* Label + Toggle pill — same row */}
            <div className="flex items-center justify-between">
              <span className="text-[#10B981] font-bold">// PLANO PRO</span>

              {/* Pill toggle */}
              <div className="flex items-center bg-[#07090E] border border-gray-700 p-0.5 gap-0.5">
                <button
                  onClick={() => setIsAnnual(false)}
                  className={`px-3 py-1 text-[10px] font-bold tracking-wider transition-all ${
                    !isAnnual
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  MENSAL
                </button>
                <button
                  onClick={() => setIsAnnual(true)}
                  className={`flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold tracking-wider transition-all ${
                    isAnnual
                      ? 'bg-[#F59E0B] text-[#07090E]'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  ANUAL
                  {!isAnnual && (
                    <span className="bg-[#F59E0B]/20 text-[#F59E0B] px-1.5 py-0.5 text-[9px] font-extrabold">
                      -{DISCOUNT_PCT}%
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Price block — fixed height, no layout shift */}
            <div className="space-y-1" style={{ minHeight: '3.5rem' }}>
              {/* Main price line */}
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-white transition-all duration-200">
                  {isAnnual
                    ? `R$ ${ANNUAL_MONTHLY_EQUIV.toFixed(2).replace('.', ',')}`
                    : 'R$ 19,90'}
                </span>
                <span className="text-xs font-normal text-gray-400">/mês</span>
                {isAnnual && (
                  <span className="bg-[#F59E0B] text-[#07090E] font-extrabold px-2 py-0.5 text-[10px]">
                    -{DISCOUNT_PCT}% OFF
                  </span>
                )}
              </div>

              {/* Annual detail line — always rendered, opacity transition to avoid height shift */}
              <div
                className="flex items-center gap-2 text-[10px] transition-opacity duration-200"
                style={{ opacity: isAnnual ? 1 : 0, pointerEvents: isAnnual ? 'auto' : 'none' }}
              >
                <span className="text-gray-500 line-through">R$ 19,90/mês</span>
                <span className="text-gray-600">·</span>
                <span className="text-[#10B981] font-bold">
                  R$ {ANNUAL_PRICE.toFixed(2).replace('.', ',')} cobrado anualmente
                </span>
                <span className="text-gray-500">
                  (economize R$ {ANNUAL_SAVINGS})
                </span>
              </div>
            </div>

            <p className="text-gray-300 font-sans text-xs leading-relaxed">
              Orçamentos ilimitados com a sua logomarca e histórico salvo na nuvem.
            </p>

            <ul className="space-y-3 pt-4 border-t border-gray-800 font-sans text-xs text-gray-200">
              <li className="flex items-center space-x-2">
                <CheckCircle2 size={16} className="text-[#10B981]" />
                <strong className="text-white">Orçamentos e recibos ILIMITADOS</strong>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle2 size={16} className="text-[#10B981]" />
                <span>Inclusão da logomarca da sua empresa</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle2 size={16} className="text-[#10B981]" />
                <span>Remoção completa de marca d'água</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle2 size={16} className="text-[#10B981]" />
                <span>Backup automático e seguro na nuvem</span>
              </li>
            </ul>
          </div>

          <a
            href="#download"
            className="w-full text-center bg-[#10B981] hover:bg-[#34D399] text-[#07090E] font-bold py-3 transition-colors block"
          >
            {isAnnual ? 'ASSINAR PLANO PRO ANUAL' : 'ASSINAR PLANO PRO'}
          </a>
        </div>

      </div>
    </section>
  );
}


export const QuickQuoteLanding: React.FC = () => {

  return (
    <div className="min-h-screen bg-[#07090E] text-[#E2E8F0] font-sans selection:bg-[#10B981] selection:text-[#07090E] relative overflow-x-hidden pb-24">
      
      {/* Background Grid Lines */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{
          backgroundImage: `
            linear-gradient(to right, #10B981 1px, transparent 1px),
            linear-gradient(to bottom, #10B981 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Radial Emerald Glows */}
      <div className="fixed top-0 right-0 w-[550px] h-[550px] bg-[#10B981]/[0.05] rounded-full blur-[140px] pointer-events-none z-0" />
      <div className="fixed bottom-0 left-0 w-[550px] h-[550px] bg-[#F59E0B]/[0.03] rounded-full blur-[160px] pointer-events-none z-0" />

      {/* Header Bar */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#07090E]/80 border-b border-[#10B981]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between font-mono text-xs">
          <a
            href="/"
            className="inline-flex items-center space-x-2 text-gray-400 hover:text-[#10B981] transition-colors"
          >
            <ArrowLeft size={16} />
            <span>VOLTAR PARA YATA.DEV</span>
          </a>

          <div className="flex items-center space-x-6">
            <a
              href="/quickquote/terms"
              className="text-gray-400 hover:text-white transition-colors flex items-center space-x-1"
            >
              <ShieldCheck size={14} />
              <span className="hidden sm:inline">TERMOS & PRIVACIDADE</span>
            </a>

            <a
              href="#download"
              className="bg-[#10B981] text-[#07090E] font-bold px-3.5 py-1.5 hover:bg-[#34D399] transition-all flex items-center space-x-1.5"
            >
              <span>BAIXAR APP</span>
              <Download size={14} />
            </a>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 space-y-20">

        {/* Hero Section */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column Text & CTAs */}
          <div className="lg:col-span-7 space-y-6">
            
            <div className="inline-flex items-center space-x-2 text-xs font-mono tracking-widest text-[#10B981] border border-[#10B981]/30 bg-[#10B981]/10 px-3 py-1">
              <Sparkles size={14} />
              <span>DISPONÍVEL PARA iOS & ANDROID</span>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-4 mb-2">
                {/* SVG Logo Icon */}
                <div className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#059669] p-3 shadow-xl shadow-[#10B981]/25 flex items-center justify-center">
                  <svg viewBox="0 0 512 512" className="w-full h-full text-white fill-current">
                    <rect x="56" y="40" width="400" height="432" rx="52" fill="none" stroke="currentColor" strokeWidth="24" />
                    <rect x="118" y="140" width="180" height="24" rx="12" />
                    <rect x="118" y="196" width="220" height="24" rx="12" />
                    <rect x="118" y="252" width="160" height="24" rx="12" />
                    <circle cx="360" cy="365" r="40" fill="#F59E0B" />
                    <text x="360" y="378" textAnchor="middle" fontSize="42" fontWeight="800" fill="#07090E">$</text>
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight">
                    QuickQuote
                  </h1>
                  <p className="text-xs sm:text-sm text-[#10B981] font-mono">
                    Orçamentos & Recibos Profissionais
                  </p>
                </div>
              </div>

              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-white">
                Crie e envie no WhatsApp em <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] via-emerald-300 to-[#F59E0B]">menos de 60 segundos.</span>
              </h2>
            </div>

            <p className="text-gray-300 text-base sm:text-lg font-light leading-relaxed max-w-2xl font-sans">
              Crie orçamentos e recibos profissionais em segundos e envie direto pelo WhatsApp. Mantenha seu negócio organizado e conquiste mais clientes!
            </p>

            {/* Store Download Badges */}
            <div id="download" className="pt-2 flex flex-wrap items-center gap-4 font-mono text-xs">
              <a
                href="#ios"
                className="inline-flex items-center space-x-3 bg-[#10B981] text-[#07090E] font-bold px-6 py-3.5 hover:bg-[#34D399] transition-all shadow-[0_0_25px_rgba(16,185,129,0.3)]"
              >
                <Smartphone size={18} />
                <div className="text-left">
                  <div className="text-[9px] uppercase tracking-wider opacity-80">Baixar na</div>
                  <div className="text-sm font-extrabold leading-none">App Store</div>
                </div>
              </a>

              <a
                href="#android"
                className="inline-flex items-center space-x-3 border border-gray-700 hover:border-[#10B981] text-gray-200 hover:text-white px-6 py-3.5 transition-all bg-[#0A0D14]"
              >
                <Smartphone size={18} />
                <div className="text-left">
                  <div className="text-[9px] uppercase tracking-wider text-gray-400">Baixar na</div>
                  <div className="text-sm font-extrabold leading-none">Google Play</div>
                </div>
              </a>
            </div>

            {/* Quick Metrics */}
            <div className="pt-4 grid grid-cols-3 gap-4 border-t border-gray-800/80 font-mono text-xs text-gray-400">
              <div>
                <span className="text-white font-bold text-base sm:text-lg block text-[#10B981]">&lt; 60s</span>
                <span>Agilidade Total</span>
              </div>
              <div>
                <span className="text-white font-bold text-base sm:text-lg block text-white">100%</span>
                <span>Offline & Seguro</span>
              </div>
              <div>
                <span className="text-white font-bold text-base sm:text-lg block text-[#F59E0B]">Gratuito</span>
                <span>5 Orçamentos/mês</span>
              </div>
            </div>

          </div>

          {/* Right Column App Showcase Card / Mockup */}
          <div className="lg:col-span-5">
            <div className="bg-[#0C111A] border-2 border-[#10B981]/30 p-6 relative font-mono shadow-2xl shadow-[#10B981]/10">
              
              <div className="absolute top-0 right-0 bg-[#10B981] text-[#07090E] text-[10px] font-bold px-3 py-1 uppercase tracking-widest">
                PRÉVIA // PROFISSIONAL
              </div>

              <div className="space-y-4 pt-2">
                <div className="border-b border-gray-800 pb-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FileText className="text-[#10B981]" size={18} />
                    <span className="text-white font-bold text-sm">ORÇAMENTO #0042</span>
                  </div>
                  <span className="text-[10px] text-gray-400">STATUS: ENVIADO</span>
                </div>

                <div className="bg-[#07090E] p-4 border border-gray-800 space-y-3 text-xs">
                  <div className="flex justify-between items-center text-gray-400">
                    <span>EMPRESA:</span>
                    <span className="text-white font-bold">Silva Instalações elétricas</span>
                  </div>
                  <div className="flex justify-between items-center text-gray-400">
                    <span>CLIENTE:</span>
                    <span className="text-white">Carlos Eduardo</span>
                  </div>
                  <div className="flex justify-between items-center text-gray-400 border-t border-gray-800/80 pt-2">
                    <span>SERVIÇO:</span>
                    <span className="text-gray-300">Instalação de Quadro Distribuição</span>
                  </div>
                  <div className="flex justify-between items-center text-[#10B981] font-bold text-sm border-t border-gray-800 pt-2">
                    <span>VALOR TOTAL:</span>
                    <span>R$ 1.250,00</span>
                  </div>
                </div>

                <div className="bg-[#10B981]/10 border border-[#10B981]/30 p-3 text-center text-xs text-[#10B981] flex items-center justify-center space-x-2">
                  <Share2 size={14} />
                  <span>Pronto para envio imediato no WhatsApp</span>
                </div>
              </div>

            </div>
          </div>

        </section>

        {/* Feature Grid Section */}
        <section className="space-y-8">
          <div className="text-center space-y-2">
            <span className="text-xs font-mono text-[#10B981] tracking-widest">// RECURSOS PRINCIPAIS</span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
              Mais agilidade na sua rotina, mais confiança para o seu cliente.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 font-mono text-xs">
            
            <div className="bg-[#0A0D15] p-6 border border-gray-800 hover:border-[#10B981]/50 transition-all space-y-3">
              <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
                <Zap size={20} />
              </div>
              <h3 className="text-sm font-bold text-white">Envio no WhatsApp</h3>
              <p className="text-gray-400 font-sans leading-relaxed">
                Preencha os itens, defina os valores e envie para o cliente em segundos pelo WhatsApp.
              </p>
            </div>

            <div className="bg-[#0A0D15] p-6 border border-gray-800 hover:border-[#10B981]/50 transition-all space-y-3">
              <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
                <WifiOff size={20} />
              </div>
              <h3 className="text-sm font-bold text-white">Funciona sem internet</h3>
              <p className="text-gray-400 font-sans leading-relaxed">
                Crie e consulte orçamentos em qualquer lugar, mesmo sem sinal de celular ou Wi-Fi.
              </p>
            </div>

            <div className="bg-[#0A0D15] p-6 border border-gray-800 hover:border-[#10B981]/50 transition-all space-y-3">
              <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
                <FileCheck2 size={20} />
              </div>
              <h3 className="text-sm font-bold text-white">Sua marca e contatos</h3>
              <p className="text-gray-400 font-sans leading-relaxed">
                Adicione seu logotipo, telefone e CPF/CNPJ para gerar documentos claros e profissionais.
              </p>
            </div>

            <div className="bg-[#0A0D15] p-6 border border-gray-800 hover:border-[#10B981]/50 transition-all space-y-3">
              <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
                <CloudCheck size={20} />
              </div>
              <h3 className="text-sm font-bold text-white">Histórico organizado</h3>
              <p className="text-gray-400 font-sans leading-relaxed">
                Acesse atendimentos anteriores, valores combinados e dados de clientes sempre que precisar.
              </p>
            </div>

          </div>
        </section>

        {/* Pricing Comparison Table */}
        <PricingSection />

        {/* Developer & Legal Footer */}
        <footer className="pt-12 border-t border-gray-800 font-mono text-xs text-gray-500 space-y-4 text-center">
          <div className="flex flex-wrap items-center justify-center gap-6 text-gray-400">
            <a href="/quickquote/terms" className="hover:text-[#10B981] transition-colors">
              POLÍTICA DE PRIVACIDADE & TERMOS
            </a>
            <span>•</span>
            <a href="/quickquote/terms#data-deletion" className="hover:text-[#10B981] transition-colors">
              INSTRUÇÕES DE EXCLUSÃO DE DADOS
            </a>
            <span>•</span>
            <a href="mailto:hi@yata.dev" className="hover:text-[#10B981] transition-colors">
              SUPORTE: HI@YATA.DEV
            </a>
          </div>

          <p>© 2026 QuickQuote — Criado por <a href="/" className="text-[#10B981] hover:underline">Luan Lima (yata.dev)</a></p>
        </footer>

      </main>
    </div>
  );
};
